import { useEffect } from "react";
import { useSnackbar } from "notistack";

import { useAppSelector, useAppDispatch } from "../../../../redux/store";

import ClearIcon from "@mui/icons-material/Clear";
import SyncIcon from "@mui/icons-material/Sync";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import {
  changeFilter,
  clearFilters,
  runCustomersSync,
} from "../../../../redux/slices/customersSlice";
import {
  Button,
  ChipMultiGroup,
  PageHeader,
  Toolbar,
} from "../../../../ui";

interface CustomersTableControlsProps {
  onBillingAddressesClick?: () => void;
}

const CustomersTableControls = ({ onBillingAddressesClick }: CustomersTableControlsProps) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.customers.filters);
  const syncStatus = useAppSelector((state) => state.customers.syncStatus);
  // The button syncs the book the portal shows, which the server decides. The
  // label says which one it is where the session can read it, and stays neutral
  // where it cannot: "Bill.com" would be wrong on the Intuit book.
  const syncMessage = useAppSelector((state) => state.customers.syncMessage);
  const syncError = useAppSelector((state) => state.customers.syncError);
  const { enqueueSnackbar } = useSnackbar();
  const source = useAppSelector((state) => state.source.source);
  const syncLabel =
    source === "ies"
      ? "Sync from Intuit"
      : source === "bill"
        ? "Sync from Bill.com"
        : "Sync now";

  const handleStatusFilter = (newStatus: string[]) => {
    dispatch(changeFilter({ name: "status", value: newStatus }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  // The sync answers with what it did — which book, which company, and whether
  // it worked — so the office is told that rather than only that it stopped.
  useEffect(() => {
    if (syncStatus === "succeeded" && syncMessage) {
      enqueueSnackbar(syncMessage, { variant: "success" });
    }
  }, [syncStatus, syncMessage, enqueueSnackbar]);

  useEffect(() => {
    if (syncStatus === "failed" && syncError) {
      enqueueSnackbar(syncError, { variant: "error" });
    }
  }, [syncStatus, syncError, enqueueSnackbar]);

  return (
    <>
      <PageHeader
        eyebrow="Customers & Billing"
        title="Customers"
        summary="The companies the office bills, the people who can pay, and the accounting book they live in."
        actions={
          <>
            {onBillingAddressesClick ? (
              <Button
                variant="secondary"
                onClick={onBillingAddressesClick}
                size="sm"
              >
                <ReceiptLongIcon sx={{ fontSize: 16 }} />
                Billing addresses
              </Button>
            ) : null}
            <Button
              size="sm"
              onClick={() => dispatch(runCustomersSync())}
              disabled={syncStatus === "loading"}
            >
              <SyncIcon sx={{ fontSize: 16 }} />
              {syncStatus === "loading" ? "Syncing…" : syncLabel}
            </Button>
          </>
        }
      />
      <Toolbar>
        <ChipMultiGroup
          values={filters.status || []}
          onChange={handleStatusFilter}
          size="sm"
          options={[
            ["active", "Active"],
            ["inactive", "Inactive"],
            ["deleted", "Deleted"],
          ]}
        />
        <Button variant="ghost" size="sm" onClick={handleClearFilters}>
          <ClearIcon sx={{ fontSize: 14 }} />
          Clear
        </Button>
      </Toolbar>
    </>
  );
};

export default CustomersTableControls;
